import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import {
  LITTER_STATES,
  LITTER_STATE_TITLES
} from 'dpl/shared/constants/shared';
import { pushParams } from 'dpl/actions/query_params';
import { LITTER_ID } from 'dpl/constants/query_params';
import { BreederProfileLitterPropTypes } from 'dpl/shared/utils/customPropTypes';
import LitterParentsVennImages from 'dpl/breeder_dashboard/components/LitterParentsVennImages';
import Button from 'dpl/common/design_system/Button';
import BreederProfileLittersSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileLittersSkeleton';
import { BADGE_LIGHT_COLORS } from 'dpl/common/design_system/utils/constants';
import CardBadge from 'dpl/components/BreederProfile/mobile/components/CardBadge';
import useLitters from 'dpl/components/BreederProfile/mobile/hooks/useLitters';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const { BORN } = LITTER_STATES;
const MAX_LITTERS_TO_SHOW = 3;
const IS_LT_MD = isLessThanBreakpoint('md');
const IMAGE_SIZE = IS_LT_MD ? '56px' : '80px';
const SMART_IMAGE_PROPS = {
  width: IMAGE_SIZE,
  height: IMAGE_SIZE,
  className: 'dib flex-none',
  isBordered: true,
  isOverlapping: true
};

function getDisplayBirthDate(litter) {
  return `${litter.status === BORN ? 'Born' : 'Due'} ${
    litter.birth_date_month_year
  }`;
}

function getLitterStatus(litter) {
  return `${LITTER_STATE_TITLES[litter.status]} ${litter.breed_name} litter`;
}

export function LitterCard({ litter, onLitterClick, analytics }) {
  return (
    <div className="BreederProfileLitterCard flex flex-column bg-white br4 ba b--subtle-gray overflow-hidden ph4 pv4 w-100">
      <LitterParentsVennImages
        litterParents={litter.parents}
        vennImageProps={SMART_IMAGE_PROPS}
      />
      <p className="font-serif font-20 mt4 mb2 mb4-md">
        {getLitterStatus(litter)}
      </p>
      <div className="flex items-center justify-between mt-auto mb4 mb6-md">
        <p className="font-14 md:font-16 tertiary">
          {getDisplayBirthDate(litter)}
        </p>
        <CardBadge className={BADGE_LIGHT_COLORS.teal}>
          Ready {litter.ready_date_month_year}
        </CardBadge>
      </div>
      <Button
        data-test-id={`litter-${litter.id}`}
        className="font-16"
        size="default"
        variant="outline"
        onClick={() => {
          onLitterClick(litter.id, analytics);
        }}
      >
        Learn more
      </Button>
    </div>
  );
}

LitterCard.propTypes = {
  analytics: PropTypes.shape({
    position: PropTypes.number
  }),
  litter: PropTypes.shape(BreederProfileLitterPropTypes).isRequired,
  onLitterClick: PropTypes.func.isRequired
};

LitterCard.defaultProps = {
  analytics: {}
};

export default function BreederProfileLittersSectionContainer({ className }) {
  const dispatch = useDispatch();

  const [isExpanded, toggleIsExpanded] = useReducer(
    expanded => !expanded,
    false
  );

  const { isFetchingCurrentLitters, upcomingLitters: litters } = useLitters();

  if (isFetchingCurrentLitters) {
    return <BreederProfileLittersSkeleton className={className} />;
  }

  if (litters.length === 0) {
    return null;
  }

  const hasMoreLitters = litters.length > MAX_LITTERS_TO_SHOW;
  const visibleLitters = litters.slice(
    0,
    isExpanded ? litters.length : MAX_LITTERS_TO_SHOW
  );

  function onLitterClick(litterId) {
    dispatch(pushParams({ [LITTER_ID]: litterId }, { hash: true }));
  }

  return (
    <section className={classnames('BreederProfileLittersSection', className)}>
      <div className="container-xxl mv8 mv20-md">
        <h4 className="mb6 mb10-md font-24 md:font-40 lh-title">
          Upcoming litters
        </h4>
        {litters.length === 1 && !IS_LT_MD ? (
          <div className="BreederProfileLitterSectionLineItem items-center w-100 gap-24 bg-white br4 ba b--subtle-gray overflow-hidden ph5 pv5">
            <LitterParentsVennImages
              litterParents={litters[0].parents}
              vennImageProps={SMART_IMAGE_PROPS}
            />
            <p className="font-serif font-20 truncate">
              {getLitterStatus(litters[0])}
            </p>

            <p className="font-16 tertiary nowrap">
              {getDisplayBirthDate(litters[0])}
            </p>
            <CardBadge
              className={classnames('nowrap', BADGE_LIGHT_COLORS.teal)}
            >
              Ready {litters[0].ready_date_month_year}
            </CardBadge>

            <Button
              data-test-id={`litter-${litters[0].id}`}
              className="font-16 ml-auto nowrap"
              size="small"
              variant="outline"
              onClick={() => {
                onLitterClick(litters[0].id);
              }}
            >
              Learn more
            </Button>
          </div>
        ) : (
          <>
            <div className="BreederProfileLittersList grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-24">
              {visibleLitters.map(litter => (
                <div key={litter.id} className="flex ws-normal">
                  <LitterCard litter={litter} onLitterClick={onLitterClick} />
                </div>
              ))}
            </div>
            {hasMoreLitters && (
              <button
                onClick={toggleIsExpanded}
                className="underline stone-700 font-16 mt6"
                type="button"
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

BreederProfileLittersSectionContainer.propTypes = {
  className: PropTypes.string
};

BreederProfileLittersSectionContainer.defaultProps = {
  className: null
};
